import React, { useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid, Environment, Stats, Box, Sphere, Html } from '@react-three/drei';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSceneStore } from '@/stores/scene-store';
import { Monitor, Grid3X3, ArrowLeft, Edit } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

interface Scene3DViewerProps {
  sceneId: string;
}

// Device 3D Model Component
function Device3D({
  position,
  rotation,
  scale,
  deviceId,
  deviceName,
  sensors = [],
}: {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  deviceId: string;
  deviceName: string;
  sensors?: any[];
}) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Main device body */}
      <Box args={[1, 0.5, 1]}>
        <meshStandardMaterial color="#64748b" />
      </Box>

      {/* Device label */}
      <Html position={[0, 1, 0]} center>
        <div className="bg-black/80 text-white px-2 py-1 rounded text-sm whitespace-nowrap">{deviceName}</div>
      </Html>

      {/* Sensors */}
      {sensors.map((sensor, index) => (
        <Sensor3D
          key={`${deviceId}-sensor-${index}`}
          position={[
            (sensor.position?.x ?? 0) * 0.1,
            (sensor.position?.y ?? 0) * 0.1 + 0.3,
            (sensor.position?.z ?? 0) * 0.1,
          ]}
          sensorType={sensor.type}
          sensorName={sensor.name}
          value={sensor.value}
        />
      ))}
    </group>
  );
}

// Sensor 3D Component
function Sensor3D({
  position,
  sensorType,
  sensorName,
  value,
}: {
  position: [number, number, number];
  sensorType: string;
  sensorName: string;
  value?: number | string;
}) {
  const getSensorIcon = () => {
    switch (sensorType) {
      case 'temperature':
        return { color: '#ef4444', icon: '🌡️' };
      case 'humidity':
        return { color: '#3b82f6', icon: '💧' };
      case 'light':
        return { color: '#eab308', icon: '💡' };
      case 'motion':
        return { color: '#8b5cf6', icon: '📷' };
      default:
        return { color: '#64748b', icon: '📡' };
    }
  };

  const { color, icon } = getSensorIcon();

  return (
    <group position={position}>
      <Sphere args={[0.1]}>
        <meshStandardMaterial color={color} />
      </Sphere>
      <Html position={[0, 0.2, 0]} center>
        <div className="bg-black/80 text-white px-1 py-0.5 rounded text-xs whitespace-nowrap">
          {icon} {sensorName}
          {value !== undefined && <div className="text-xs opacity-75">{value}</div>}
        </div>
      </Html>
    </group>
  );
}

// Main Scene3DViewer Component
export function Scene3DViewer({ sceneId }: Scene3DViewerProps) {
  const { scenes, setCurrentScene, currentScene } = useSceneStore();
  const [showGrid, setShowGrid] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load scene from store
    const scene = scenes.find((s) => s.id === sceneId);
    if (scene && scene.id !== currentScene?.id) {
      setCurrentScene(scene);
    }
  }, [sceneId, scenes, currentScene?.id, setCurrentScene]);

  if (!currentScene) {
    return (
      <div className="flex items-center justify-center h-full">
        <div>Loading scene...</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <Canvas camera={{ position: [10, 10, 10], fov: 50 }} shadows>
        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
        <pointLight position={[-10, -10, -10]} intensity={0.3} />

        {/* Environment */}
        <Environment preset="city" />

        {/* Grid */}
        {showGrid && (
          <Grid
            args={[20, 20]}
            position={[0, 0, 0]}
            cellSize={1}
            cellThickness={0.5}
            cellColor="#64748b"
            sectionSize={5}
            sectionThickness={1}
            sectionColor="#374151"
            fadeDistance={30}
          />
        )}

        {/* Devices */}
        {currentScene.devicePlacements?.map((devicePlacement) => (
          <Device3D
            key={devicePlacement.id}
            position={[devicePlacement.position.x, devicePlacement.position.y, devicePlacement.position.z]}
            rotation={[devicePlacement.rotation.x, devicePlacement.rotation.y, devicePlacement.rotation.z]}
            scale={[devicePlacement.scale.x, devicePlacement.scale.y, devicePlacement.scale.z]}
            deviceId={devicePlacement.deviceId}
            deviceName={devicePlacement.deviceName ?? devicePlacement.deviceId}
            sensors={devicePlacement.sensors || []}
          />
        ))}

        {/* Controls */}
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          dampingFactor={0.05}
          screenSpacePanning={false}
          minDistance={3}
          maxDistance={50}
        />

        {/* Stats */}
        {showStats && <Stats />}
      </Canvas>

      {/* Header */}
      <Card className="absolute top-4 left-4 z-10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Scene Viewer</CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate({ to: '/dashboard/scenes/$sceneId/editor', params: { sceneId } })}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/dashboard/scenes">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* View Controls */}
      <Card className="absolute top-4 right-4 z-10">
        <CardContent className="p-2">
          <div className="flex items-center gap-2">
            <Button size="sm" variant={showGrid ? 'default' : 'outline'} onClick={() => setShowGrid(!showGrid)}>
              <Grid3X3 className="w-4 h-4" />
            </Button>

            <Button size="sm" variant={showStats ? 'default' : 'outline'} onClick={() => setShowStats(!showStats)}>
              <Monitor className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="absolute bottom-4 left-4 z-10">
        <CardContent className="p-3">
          <div className="text-sm text-muted-foreground">
            <div className="font-medium mb-1">Navigation:</div>
            <div>• Mouse: Orbit • Scroll: Zoom • Right-click: Pan</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
